import { Skeleton } from '@mantine/core';
import { match } from 'ts-pattern';

import testIds from '~/domains/home/testing.const';

import { NEXT_BATCH_SIZE, RAIL_SKELETON_HEIGHT } from '../../constants/rail';
import { CarouselHeader } from '../CarouselHeader/CarouselHeader';
import { CarouselPills } from '../CarouselPills/CarouselPills';
import { ContestRail } from '../ContestRail/ContestRail';

import { useStyles } from './CondensedCarouselView.styles';
import type { UseCondensedCarouselModuleReturn } from './useCondensedCarouselModule';

const HEADER_SKELETON_HEIGHT = 24;
const PILLS_SKELETON_HEIGHT = 34;

export type CondensedCarouselViewProps = UseCondensedCarouselModuleReturn & {
  title: string;
  iconUrl?: string;
};

function ModuleSkeleton({ showPills }: { showPills: boolean }) {
  const { classes } = useStyles();

  return (
    <>
      <Skeleton height={HEADER_SKELETON_HEIGHT} width="40%" radius="sm" />
      {showPills && <Skeleton height={PILLS_SKELETON_HEIGHT} width="60%" radius="xl" />}
      <div className={classes.skeletonRail}>
        {Array.from({ length: NEXT_BATCH_SIZE }, (_, index) => (
          <Skeleton key={index} height={RAIL_SKELETON_HEIGHT} radius="lg" />
        ))}
      </div>
    </>
  );
}

export function CondensedCarouselView({
  elementRef,
  displayState,
  title,
  iconUrl,
  showPills,
  visibleTabs,
  activeTab,
  selectTab,
  rail,
  scrollerRef,
  trackRef,
  sentinelRef,
  viewMore,
  getPrefetchConfig,
  promoSource,
  railId,
  isContestThumbnailShown,
  isCountdownPillEnabled,
}: CondensedCarouselViewProps) {
  const { classes, cx } = useStyles();

  return (
    <section
      ref={elementRef}
      className={cx(classes.module, displayState === 'hidden' && classes.hidden)}
      data-test-id={testIds.components.condensedCarousels.module}
    >
      {match(displayState)
        .with('hidden', () => null)
        .with('pending', () => <ModuleSkeleton showPills={showPills} />)
        .with('ready', () => (
          <>
            <CarouselHeader
              iconUrl={iconUrl}
              title={title}
              viewMoreHref={viewMore.href}
              labelParts={viewMore.labelParts}
            />
            {showPills && (
              <CarouselPills
                carouselTitle={title}
                railId={railId}
                tabs={visibleTabs}
                activeValue={activeTab.value}
                onSelect={selectTab}
                getPrefetchConfig={getPrefetchConfig}
              />
            )}
            <ContestRail
              id={railId}
              isTabPanel={showPills}
              tabValue={activeTab.value}
              contests={rail.contests}
              skeletonCount={rail.skeletonCount}
              isInitialLoading={rail.isInitialLoading}
              scrollerRef={scrollerRef}
              trackRef={trackRef}
              sentinelRef={sentinelRef}
              promoSource={promoSource}
              isContestThumbnailShown={isContestThumbnailShown}
              isCountdownPillEnabled={isCountdownPillEnabled}
            />
          </>
        ))
        .exhaustive()}
    </section>
  );
}
